<script setup>
import { ref, watch, onMounted } from "vue";
import FormLabelledInput from "../form/LabelledInput.vue";
import ChargemapSearchDropdown from "../ChargemapSearchDropdown.vue";
import FormSwitchSelector from "../form/SwitchSelector.vue";
import ChargemapB2CButton from "../ChargemapB2CButton.vue";
const props = defineProps({
  vehicle: {
    type: Object,
    default: void 0
  },
  brands: {
    type: Array,
    required: true
  },
  models: {
    type: Array,
    default: () => []
  },
  versions: {
    type: Array,
    default: () => []
  },
  isUpdate: {
    type: Boolean,
    default: false
  },
  isDisabled: {
    type: Boolean,
    default: false
  }
});
const emits = defineEmits(["submit", "updateModels", "updateVersions"]);
const vehicleBrand = ref(null);
const vehicleModel = ref(null);
const vehicleVersion = ref(null);
const isDefault = ref(false);
const isInitialLoad = ref(true);
onMounted(() => {
  if (props.isUpdate && props.vehicle) {
    vehicleBrand.value = {
      id: props.vehicle.brandId,
      value: props.vehicle.brandName
    };
    vehicleModel.value = {
      id: props.vehicle.modelId,
      value: props.vehicle.modelName
    };
    vehicleVersion.value = {
      id: props.vehicle.versionId,
      value: props.vehicle.versionName
    };
    isDefault.value = props.vehicle.isDefault;
    setTimeout(() => {
      isInitialLoad.value = false;
    }, 0);
  } else {
    isInitialLoad.value = false;
  }
});
const handleSubmit = () => {
  if (vehicleBrand.value && vehicleModel.value && vehicleVersion.value) {
    emits("submit", {
      id: props.vehicle?.id,
      userId: props.vehicle?.userId,
      brandId: Number(vehicleBrand.value.id),
      brandName: vehicleBrand.value.value,
      modelId: Number(vehicleModel.value.id),
      modelName: vehicleModel.value.value,
      versionId: Number(vehicleVersion.value.id),
      versionName: vehicleVersion.value.value,
      isDefault: isDefault.value
    });
  }
};
watch(vehicleBrand, (newBrand) => {
  if (!isInitialLoad.value) {
    vehicleModel.value = null;
    vehicleVersion.value = null;
  }
  if (newBrand) {
    emits("updateModels", Number(newBrand.id));
  }
});
watch(vehicleModel, (newModel) => {
  if (!isInitialLoad.value) {
    vehicleVersion.value = null;
  }
  if (newModel && vehicleBrand.value) {
    emits(
      "updateVersions",
      Number(newModel.id),
      Number(vehicleBrand.value.id)
    );
  }
});
</script>

<template>
    <div class="w-full flex flex-col items-center gap-6">
        <div class="w-full flex flex-col items-center gap-2">
            <IconCar class="size-12 md:size-16 text-icon-secondary-branding" />
            <span
                class="text-[17px] md:text-[22px] text-header-dark font-bold leading-5 md:leading-[25px]"
            >
                {{
                    props.isUpdate ? $t("user_vehicles.titles.update") : $t("user_vehicles.titles.add")
                }}
            </span>
        </div>

        <div class="w-full flex flex-col gap-4 items-center">
            <FormLabelledInput
                :label="$t('user_vehicles.common.brand')"
                :is-disabled
            >
                <ChargemapSearchDropdown
                    v-model="vehicleBrand"
                    :options="props.brands"
                    :is-disabled
                />
            </FormLabelledInput>

            <FormLabelledInput
                :label="$t('user_vehicles.common.model')"
                :is-disabled="!vehicleBrand || isDisabled"
            >
                <ChargemapSearchDropdown
                    v-model="vehicleModel"
                    :options="props.models"
                    :is-disabled="!vehicleBrand || isDisabled"
                />
            </FormLabelledInput>

            <FormLabelledInput
                :label="$t('user_vehicles.common.version')"
                :is-disabled="!vehicleModel || isDisabled"
            >
                <ChargemapSearchDropdown
                    v-model="vehicleVersion"
                    :options="props.versions"
                    :is-disabled="!vehicleModel || isDisabled"
                />
            </FormLabelledInput>

            <FormSwitchSelector
                v-model="isDefault"
                :label="$t('user_vehicles.common.toggle_default')"
                :is-disabled="!vehicleVersion || isDisabled"
            />
        </div>

        <ChargemapB2CButton
            class="w-full"
            type="primary"
            :is-disabled="!vehicleVersion || isDisabled"
            @click="handleSubmit"
        >
            {{ $t("user_vehicles.common.submit") }}
        </ChargemapB2CButton>
    </div>
</template>
